/**
 * useAccountLimits
 *
 * Mirrors the web's getMaxLimit() function and the per-resource limit checks
 * in Pages/Medications, Pages/Projects, Components/ProjectMembers, Pages/Documents.
 *
 * Web: getMaxLimit(name, coupon.metadata, account.subscription, account.user_plan, whitelabel)
 *       = Math.max(couponMeta[name], subscription[name], user_plan[name], whitelabel[name])
 *
 * Mobile: coupon metadata and whitelabel are not readily available without a Stripe customer
 * fetch, so we use max(subscription[name], user_plan[name]) which covers 99% of cases.
 *
 * ── Counting semantics ────────────────────────────────────────────────────────
 * Medications:  account-wide total across all projects (matches billing screen display).
 *               totalMedicationCount is summed from state.projects.list[*].medication_count.
 * Projects:     account-wide total (Object.keys(state.projects.list).length).
 * Members:      per-project count passed by the caller (limit applies per care plan).
 * Storage:      account-wide total bytes across all projects (matches billing screen display).
 */
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../store';

// `-1` is the codebase-wide convention for "unlimited" on numeric
// entitlement keys (mirrors backend account-metadata.js#resolveLimit).
// Raw Math.max would treat it as a smaller-than-any-positive value
// — wrong direction. Map -1 → Infinity for the comparison, then
// re-encode Infinity → -1 on the way out so callers keep the
// convention.
const UNLIMITED = -1;
function asLimit(v: unknown): number {
  const n = typeof v === 'number' ? v : parseInt(String(v), 10);
  if (!Number.isFinite(n)) return 0;
  return n === UNLIMITED ? Infinity : n;
}
function getMax(
  sub: Record<string, any>,
  plan: Record<string, any>,
  name: string,
): number {
  const max = Math.max(asLimit(sub?.[name]), asLimit(plan?.[name]));
  return max === Infinity ? UNLIMITED : max;
}
// For partner-invited accounts the subscription already has metadata-derived
// ceilings applied by the backend (get-accounts controller). However, user_plan
// values from the Stripe plan template are often -1 (unlimited) and Math.max
// would pick unlimited over the partner's finite ceiling. When we detect a
// partner subscription via metadata, use the subscription value directly
// instead of taking the max with user_plan.
function getPartnerConstrainedMax(
  sub: Record<string, any>,
  plan: Record<string, any>,
  name: string,
): number {
  const meta = sub?.metadata && typeof sub.metadata === 'object' ? sub.metadata as Record<string, any> : null;
  const isPartner = !!(meta?.partner_referral_base || meta?.partner_cognito_id);
  if (isPartner && sub?.[name] != null) {
    const v = asLimit(sub[name]);
    return v === Infinity ? UNLIMITED : v;
  }
  return getMax(sub, plan, name);
}

export interface AccountLimits {
  // ── Limits ───────────────────────────────────────────────────────────────
  maxProjects: number;
  maxMembers: number;
  maxMedications: number;
  maxStorageBytes: number;
  /** Mentions per chat message (from user_plan/subscription `mentions`). */
  maxMentions: number;

  // ── Account-wide usage (derived from state.projects.list) ────────────────
  projectCount: number;
  totalStorageBytes: number;
  totalMedicationCount: number;
  /** Active member cognito-id array length (account.users) */
  accountMemberCount: number;

  // ── Boolean gates ─────────────────────────────────────────────────────────
  /** true when account has room for another care plan */
  canAddProject: boolean;
  /**
   * true when account-wide medication total is below the plan limit.
   * Uses totalMedicationCount (sum across all projects) so it matches the
   * "X / Y meds" display on the billing screen.
   */
  canAddMedication: boolean;
  /**
   * true when the given project's notified-member count is below the plan limit.
   * Pass the current project's notified-member count.
   */
  canAddMember: (currentProjectMemberCount: number) => boolean;
  /** true when uploading `bytes` more would not exceed the storage limit */
  canUploadBytes: (bytes: number) => boolean;
  /** true when the chat-message mention array is below the plan limit. */
  canAddMention: (currentMentionCount: number) => boolean;
}

export function useAccountLimits(): AccountLimits {
  const account = useSelector((s: RootState) => s.session.account) as any;
  const projectsMap = useSelector((s: RootState) => (s as any).projects?.list ?? {}) as Record<string, any>;

  return useMemo(() => {
    const sub: Record<string, any> = account?.subscription ?? {};
    const plan: Record<string, any> = account?.user_plan ?? {};

    const maxProjects = getPartnerConstrainedMax(sub, plan, 'plans');
    const maxMembers = getPartnerConstrainedMax(sub, plan, 'members');
    const maxMedications = getPartnerConstrainedMax(sub, plan, 'medications');
    const maxStorageBytes = getPartnerConstrainedMax(sub, plan, 'storage_limit');
    const maxMentions = getPartnerConstrainedMax(sub, plan, 'mentions');

    const projectValues = Object.values(projectsMap);
    const projectCount = projectValues.length;
    const totalStorageBytes = projectValues.reduce((acc, p) => acc + (Number(p.document_usage) || 0), 0);
    const totalMedicationCount = projectValues.reduce((acc, p) => acc + (parseInt(p.medication_count) || 0), 0);
    const accountMemberCount = Array.isArray(account?.users) ? account.users.length : 0;

    return {
      maxProjects,
      maxMembers,
      maxMedications,
      maxStorageBytes,
      maxMentions,

      projectCount,
      totalStorageBytes,
      totalMedicationCount,
      accountMemberCount,

      // Two pass-through cases:
      //   - maxX === 0       → plan data hasn't loaded yet, allow
      //   - maxX === -1      → unlimited per codebase convention, allow
      // Without the -1 carve-out a paid user (medications: -1) would see
      // "32 / -1 medications" and be blocked from adding more.
      canAddProject:
        maxProjects === 0 || maxProjects === UNLIMITED || projectCount < maxProjects,
      canAddMedication:
        maxMedications === 0 || maxMedications === UNLIMITED || totalMedicationCount < maxMedications,
      canAddMember: (currentCount: number) =>
        maxMembers === 0 || maxMembers === UNLIMITED || currentCount < maxMembers,
      canUploadBytes: (bytes: number) =>
        maxStorageBytes === 0 || maxStorageBytes === UNLIMITED || totalStorageBytes + bytes <= maxStorageBytes,
      canAddMention: (currentCount: number) =>
        maxMentions === 0 || maxMentions === UNLIMITED || currentCount < maxMentions,
    };
  }, [account, projectsMap]);
}
